import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button, Form, Spinner, Alert, Row, Col } from "react-bootstrap";
import useApi from "../hooks/useApi";

const ForgotPasswordModal = ({ show, handleClose, userType = "user"  }) => {
  const { bearerAuth, publicAuth } = useApi();
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [type, setType] = useState(userType);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [notRobot, setNotRobot] = useState(false);
  const [touched, setTouched] = useState({ email: false, otp: false });
  const [countdown, setCountdown] = useState(0);

  // Countdown timer for OTP resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      resetForm();
    }
  }, [show]);

  useEffect(() => {
    setType(userType);
  }, [userType]);

  // Email validation
  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  // Password validation
  const validatePassword = () => {
    if (!password) return "Password is required";
    if (password.length < 6) return "Password must be at least 6 characters";
    if (password !== confirmPassword) return "Passwords do not match";
    return "";
  };

  // Step 1: Send OTP to email
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!email) {
      setError("Email is required");
      return;
    }

    if (!isValidEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    if (!notRobot) {
      setError("Please confirm you are not a robot");
      return;
    }

    try {
      setLoading(true);
      
      const response = await publicAuth("/forgot-password", "POST", {
        email: email,
        type: type
      });

      if (response.data?.status) {
        setSuccess("OTP has been sent to your email.");
        setTimeout(() => {
          setStep(2);
          setSuccess("");
          setCountdown(60); // 60 seconds countdown
        }, 1500);
      } else {
        setError(response.data?.message || "Failed to send OTP");
      }
    } catch (err) {
      setError(
        err?.message || 
        err?.response?.data?.message ||
        "Unable to send OTP. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!otp || otp.length !== 6) {
      setError("Please enter a valid 6-digit OTP");
      return;
    }

    try {
      setLoading(true);
      
      const response = await publicAuth("/verify-reset-otp", "POST", {
        email: email,
        otp: otp,
        type: type,
      });

      if (response.data?.status) {
        setResetToken(response.data?.reset_token || response.data?.token || "");
        setSuccess("OTP verified successfully!");
        setTimeout(() => {
          setStep(3);
          setSuccess("");
        }, 1500);
      } else {
        setError(response.data?.message || "Invalid OTP");
      }
    } catch (err) {
      setError(
        err?.message ||
        err?.response?.data?.message ||
        "Unable to verify OTP. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Update Password
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const passwordError = validatePassword();
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (!resetToken) {
      setError("Reset token is missing. Please restart the process.");
      return;
    }

    try {
      setLoading(true);
      
      const response = await bearerAuth("/update-password", "POST", {
        reset_token: resetToken,
        password: password,
        password_confirmation: confirmPassword
      });

      if (response.data?.status) {
        setSuccess("Password updated successfully! You can now login with your new password.");
        setTimeout(() => {
          resetForm();
          handleClose();
        }, 2000);
      } else {
        setError(response.data?.message || "Failed to update password");
      }
    } catch (err) {
      setError(
        err?.message ||
        err?.response?.data?.message ||
        "Unable to update password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (countdown > 0) return;
    
    setError("");
    setSuccess("");
    
    try {
      setLoading(true);
      
      const response = await publicAuth("/forgot-password", "POST", {
        email: email,
        type: type
      });

      if (response.data?.status) {
        setSuccess("New OTP sent to your email.");
        setCountdown(60);
      } else {
        setError(response.data?.message || "Failed to resend OTP");
      }
    } catch (err) {
      setError(
        err?.message ||
        err?.response?.data?.message ||
        "Unable to resend OTP. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Reset everything
  const resetForm = useCallback(() => {
    setStep(1);
    setEmail("");
    setOtp("");
    setPassword("");
    setConfirmPassword("");
    setResetToken("");
    setType(userType);
    setLoading(false);
    setError("");
    setSuccess("");
    setNotRobot(false);
    setTouched({ email: false, otp: false });
    setCountdown(0);
  }, [userType]);

  // Go back to previous step
  const goBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setError("");
      setSuccess("");
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    resetForm();
    handleClose();
  };

  return (
    <Modal 
      show={show} 
      onHide={handleModalClose} 
      centered 
      backdrop="static" 
      size={step === 3 ? "lg" : "md"}
      style={{ zIndex: 1060 }} // Login modal से higher z-index
    >
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          {step === 1 && "Forgot Password"}
          {step === 2 && "Verify OTP"}
          {step === 3 && "Reset Password"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Progress Indicator */}
        {step > 1 && (
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center">
              {[1, 2, 3].map((s) => (
                <React.Fragment key={s}>
                  <div className={`rounded-circle ${s <= step ? 'bg-primary' : 'bg-secondary'} text-white d-flex align-items-center justify-content-center`}
                    style={{ width: '40px', height: '40px' }}>
                    {s}
                  </div>
                  {s < 3 && (
                    <div className={`flex-grow-1 ${s < step ? 'bg-primary' : 'bg-secondary'}`} style={{ height: '2px' }} />
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="d-flex justify-content-between mt-2">
              <small>Enter Email</small>
              <small>Verify OTP</small>
              <small>New Password</small>
            </div>
          </div>
        )}

        {success && <Alert variant="success">{success}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={
          step === 1 ? handleSendOTP :
          step === 2 ? handleVerifyOTP :
          handleUpdatePassword
        }>
          {/* Step 1: Email Input */}
          {step === 1 && (
            <>
              <p className="text-muted text-center mb-4">
                Enter your registered email to receive a password reset OTP.
              </p>

              <Form.Group className="mb-3">
                <Form.Label>Account Type</Form.Label>
                <Form.Select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  disabled={loading}
                >
                  <option value="user">User</option>
                  <option value="vendor">Vendor</option>
                  <option value="school">School</option>
                  <option value="agent">Agent</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="example@gmail.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setTouched({ ...touched, email: true })}
                  isInvalid={touched.email && email && !isValidEmail(email)}
                  disabled={loading}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  Invalid email address
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Check
                  type="checkbox"
                  label="I'm not a robot"
                  checked={notRobot}
                  onChange={(e) => setNotRobot(e.target.checked)}
                  disabled={loading}
                  required
                />
              </Form.Group>
            </>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <>
              <p className="text-muted text-center mb-4">
                Enter the 6-digit OTP sent to <strong>{email}</strong>
              </p>

              <Form.Group className="mb-3">
                <Form.Label>OTP</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 6) setOtp(value);
                  }}
                  onBlur={() => setTouched({ ...touched, otp: true })}
                  isInvalid={touched.otp && otp && otp.length !== 6}
                  disabled={loading}
                  maxLength={6}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  OTP must be 6 digits
                </Form.Control.Feedback>
              </Form.Group>

              <div className="d-flex justify-content-between align-items-center mb-4">
                <Button
                  variant="link"
                  onClick={handleResendOTP}
                  disabled={loading || countdown > 0}
                  className="p-0"
                >
                  {countdown > 0 ? `Resend OTP in ${countdown}s` : "Resend OTP"}
                </Button>
              </div>
            </>
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <>
              <p className="text-muted text-center mb-4">
                Create a new password for your account
              </p>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>New Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Enter new password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      required
                      isInvalid={password && password.length < 6}
                    />
                    <Form.Control.Feedback type="invalid">
                      Minimum 6 characters required
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      At least 6 characters
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Confirm Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={loading}
                      required
                      isInvalid={confirmPassword && password !== confirmPassword}
                    />
                    <Form.Control.Feedback type="invalid">
                      Passwords do not match
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Alert variant="info" className="mt-3">
                <i className="bi bi-info-circle me-2"></i>
                After resetting, you'll be redirected to login page.
              </Alert>
            </>
          )}

          {/* Action Buttons */}
          <div className="d-flex justify-content-between gap-2 mt-4">
            <div>
              {step > 1 && (
                <Button
                  variant="outline-secondary"
                  onClick={goBack}
                  disabled={loading}
                >
                  Back
                </Button>
              )}
            </div>
            
            <div className="d-flex gap-2">
              <Button
                variant="secondary"
                onClick={handleModalClose}
                disabled={loading}
              >
                Cancel
              </Button>

              <Button
                variant="primary"
                type="submit"
                disabled={
                  loading ||
                  (step === 1 && (!email || !notRobot || !isValidEmail(email))) ||
                  (step === 2 && (!otp || otp.length !== 6)) ||
                  (step === 3 && (!password || !confirmPassword || validatePassword()))
                }
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {step === 1 && "Sending..."}
                    {step === 2 && "Verifying..."}
                    {step === 3 && "Updating..."}
                  </>
                ) : (
                  <>
                    {step === 1 && "Send OTP"}
                    {step === 2 && "Verify OTP"}
                    {step === 3 && "Update Password"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </Form>
      </Modal.Body>

      {step === 1 && (
        <Modal.Footer className="justify-content-center">
          <Button
            variant="link"
            className="text-decoration-none"
            onClick={handleModalClose}
          >
            Back to Login
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default ForgotPasswordModal;
